import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SubpageLayout from "@/components/SubpageLayout";
import { P, Emphasis, Section, Sub } from "@/components/Prose";
import { scrollToSection } from "@/App";

const OmOs = () => (
  <SubpageLayout overline="TRAUMEBEVIDST UDVIKLING" title="Om os" testId="page-om-os">
    <Section title="Om Traumebevidst Udvikling">
      <P>
        Traumebevidst Udvikling blev stiftet i 2026 med et ønske om at bidrage til nye og mere
        sammenhængende måder at forstå og arbejde med menneskers trivsel, psykiske lidelse og
        udvikling på.
      </P>
      <P>
        Viden om traumer overvejes i højere grad på tværs af blandt andet social-, sundheds-,
        psykiatri- og skoleområdet. Traumebevidst Udvikling vil bidrage til denne bevægelse og
        til, at vi går fra viden til egentlige forandringer i praksis.
      </P>
      <P>
        Vi vil sikre, at traumebevidsthed ikke blot bliver noget, vi ved, men noget, der kan
        genfindes i vores møder, handlinger, organisering og de rammer, vi skaber.
      </P>
      <P>Med én vigtig opmærksomhed:</P>
      <Emphasis>
        Traumebevidsthed skal udvide vores forståelse af mennesker og systemer – ikke blive endnu
        en snæver forklaring på dem.
      </Emphasis>
      <p className="pt-2">
        <Link
          to="/vores-tilgang"
          data-testid="om-os-tilgang-link"
          className="group inline-flex items-center gap-2 text-sm font-medium text-[#3F5A47] transition-colors duration-300 hover:text-[#B46A45]"
        >
          Læs mere om vores tilgang
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </p>
    </Section>

    <Section title="Menneskerne bag">
      <div className="space-y-14">
        <Sub
          title="Sabrina Brødsgaard Nielsen"
          subline="Psykolog og stifter af Traumebevidst Udvikling"
          image="/brand/sabrina.jpg"
          socials={[
            { type: "linkedin", url: "https://www.linkedin.com/in/sabrinabn" },
            { type: "facebook", url: "https://www.facebook.com/profile.php?id=61574097250649&locale=da_DK" },
            { type: "instagram", url: "https://www.instagram.com/sabrinapsykolog/" },
          ]}
        >
          <P>
            Sabrina Brødsgaard Nielsen er psykolog med mere end 10 års erfaring inden for
            traumeterapi, undervisning, supervision og implementering af traumebevidste tilgange.
          </P>
          <P>
            Hun har tidligere haft ledelses- og behandlingsansvar for børne-, unge- og asylområdet
            på et rehabiliteringscenter for traumatiserede, hvor hun blandt andet udviklede og
            etablerede en afdeling med helhedsorienteret behandling og støtte til børn, unge og
            familier. Hun har desuden arbejdet som chefkonsulent med speciale i traumer.
          </P>
          <P>
            Gennem sit arbejde har Sabrina set, hvor stor betydning vores forståelser, sprog og
            organisatoriske rammer har for, hvordan mennesker bliver mødt, og hvilke muligheder de
            får. Det har ført hendes arbejde fra terapirummet og ud i de organisationer og
            systemer, mennesker er en del af.
          </P>
          <P>
            Hun har gennem forskning, foreningsarbejde, fagpolitisk og politisk samarbejde samt
            bidrag til den offentlige debat arbejdet for at styrke forståelsen af traumer,
            menneskers livssammenhænge og behovet for systemisk forandring.
          </P>
          <P>
            Sabrina er særligt optaget af, hvordan traumebevidsthed omsættes til praksis i
            relationer, faglige kulturer, ledelse og organisatoriske rammer. Et særligt fokus er at
            skabe større ligeværd mellem faglig viden og levede erfaringer og styrke mulighederne
            for, at mennesker sammen kan udvikle den praksis, de er en del af.
          </P>
          <P>
            Det arbejde blev afsættet for etableringen af Traumebevidst Udvikling.
          </P>
        </Sub>

        <Sub
          title="Anders Sørensen"
          subline="Psykolog, ph.d. i psykiatri og samarbejdspartner i Traumebevidst Udvikling"
          image="/brand/anders.jpg"
          socials={[
            { type: "linkedin", url: "https://www.linkedin.com/in/anders-s%C3%B8rensen-215664127/" },
            { type: "facebook", url: "https://www.facebook.com/AndersSorensenPhD?locale=da_DK" },
            { type: "instagram", url: "https://www.instagram.com/anders_sorensen_phd/" },
          ]}
        >
          <P>
            Anders Sørensen er psykolog og ph.d. i psykiatri og har gennem mere end 10 år
            beskæftiget sig med traumer, psykofarmaka og alternativer til traditionelle
            psykiatriske sygdomsforståelser.
          </P>
          <P>
            En del af hans arbejde retter sig mod de strukturer og faglige rammer, mennesker møder
            i social- og sundhedsfaglige tilbud. Her arbejder han for at styrke sikkerheden og
            mindske risikoen for skade gennem informeret samtykke, adgang til opdateret viden og
            forskning samt tidssvarende retningslinjer.
          </P>
          <P>
            Lighed i adgang til viden er en vigtig del af dette arbejde. Anders er blandt andet
            forfatter til bogen Noget I bør vide... Om udtrapning af psykofarmaka og står bag
            podcasten Noget I bør vide. Han underviser desuden fagpersoner i farmakologi,
            udtrapning og traumeterapi. Fokus er mindre på faggrænser og mere på, at relevant viden
            kommer derud, hvor den skal leve og kan gøre en forskel for mennesker.
          </P>
          <P>
            Anders arbejder ud fra en forståelse af menneskers reaktioner og signaler som
            meningsfulde. Det betyder dog ikke, at alle signaler skal graves i for at finde
            dybereliggende traumer eller føre til større livsændringer. Han er optaget af at kunne
            skelne mellem traumereaktioner, der kalder på bearbejdning, signaler på belastende
            livsomstændigheder og signaler, der er opstået som følge af kemiske ændringer,
            eksempelvis i forbindelse med udtrapning af psykofarmaka. Nogle gange kan arbejdet også
            bestå i at genkende et signal som ubehag, der kan rummes, og finde måder at være i det
            uden nødvendigvis at handle på det.
          </P>
        </Sub>

        <Sub
          title="Helena Dahl Bendixen"
          subline="Erfaringsfaglig underviser, recoverymentor og samarbejdspartner i Traumebevidst Udvikling"
          image="/brand/helena.jpg"
          socials={[
            { type: "linkedin", url: "https://www.linkedin.com/in/helena-dahl-bendixen-105271286/" },
          ]}
        >
          <P>
            Helena Dahl Bendixen er erfaringsfaglig underviser og recoverymentor. Hun har gennem
            flere år arbejdet på tværs af forskellige systemer med recovery, traumer og
            traumebevidst tilgang. Her har hun arbejdet for, at mennesker får deres stemme hørt,
            både i deres egne møder med systemerne og i forandringsprocesserne deri.
          </P>
          <P>
            Helena er optaget af ligeværd i møder og processer. Det gælder også i udviklingen af
            viden. Hun ønsker ikke selv at blive betegnet som erfaringsekspert, fordi
            optagetheden af ekspertstatus kan komme til at stå i vejen for ligeværd og for, at
            forskellige former for viden får plads.
          </P>
          <P>
            For Helena begynder meget med værdighed og oprigtig nysgerrighed på det menneske, man
            står overfor. Hun er optaget af at høre andre mennesker og forstå, hvad der ligger bag
            deres reaktioner.
          </P>
          <P>
            Det hænger også sammen med hendes egne erfaringer. Helena har oplevet, hvordan
            firkantede forståelser kan begrænse og gøre skade, og hvilken forskel det kan gøre at
            blive mødt med værdighed og få sin egen viden om sit liv taget alvorligt. Den erfaring
            tager hun med sig i sit arbejde og i mødet med andre.
          </P>
          <P>
            Helena underviser både fagpersoner og mennesker med levede erfaringer i traumebevidst
            tilgang og bidrager til udvikling af materialer og implementering på tværs af
            forskellige kontekster.
          </P>
        </Sub>

        <Sub
          title="Mads Dahlgaard"
          subline="Psykolog og samarbejdspartner i Traumebevidst Udvikling"
          image="/brand/mads.jpg"
          socials={[
            { type: "linkedin", url: "https://www.linkedin.com/in/madsdahlgaard/" },
          ]}
        >
          <P>
            Mads Dahlgaard er uddannet psykolog og har gennem mere end 15 år arbejdet med
            psykoterapi, hvor han har haft et særligt fokus på traumer og traumereaktioner. En stor
            del af hans arbejde har desuden været rettet mod at udvikle, systematisere og formidle
            viden på området.
          </P>
          <P>
            Siden 2009 har Mads haft selvstændig klinisk praksis, og siden 2012 har han været leder
            af Center for Trivsel og Velvære. Han har desuden erfaring fra socialpsykiatrien og
            mange års erfaring med undervisning, supervision og kompetenceudvikling i både ind- og
            udland.
          </P>
          <P>
            En gennemgående optagethed i Mads' arbejde er at gøre kompleks faglig viden forståelig
            og anvendelig i praksis. Han har bidraget til udbredelsen af traumebevidst tilgang
            (TBT) i Danmark og har udgivet en fagbog om træning og udvikling af psykoterapeutiske
            færdigheder.
          </P>
          <P>
            Mads siger gerne, at man skal praktisere det, man prædiker. For ham betyder det, at de
            værdier og principper, vi underviser i, også skal kunne genfindes i vores egen praksis
            og i den måde, vi møder andre mennesker på. Erfaringsfagligheden står derfor centralt i
            hans arbejde. Viden om mennesker kan ikke alene skabes om dem, men må også udvikles med
            dem.
          </P>
          <P>
            Ligeværd i viden er en anden gennemgående ambition. Mads arbejder aktivt for at gøre
            viden, der traditionelt har været forbeholdt fagpersoner, tilgængelig for flere. Gennem
            blandt andet podcast, sociale medier og en serie, der åbner døren til det terapeutiske
            rum, formidler han psykologisk og terapeutisk viden, så den ikke alene tilhører
            fagpersoner, men også bliver tilgængelig for de mennesker, den handler om.
          </P>
          <P>
            Mads arbejder desuden med filosofisk praksis som supplement til psykoterapi og med,
            hvordan filosofiske perspektiver kan bidrage til at undersøge og forstå menneskelige
            erfaringer.
          </P>
        </Sub>
      </div>
    </Section>

    <Section title="Kontakt">
      <P>E-mail: kontakt@traumebevidstudvikling.dk</P>
      <P>CVR: 42608769</P>
      <p className="pt-4">
        <button
          onClick={() => scrollToSection("#kontakt")}
          data-testid="om-os-cta-kontakt"
          className="rounded-full bg-[#3F5A47] px-8 py-3 text-sm font-medium text-[#FAF7F1] transition-colors duration-300 hover:bg-[#2F3338]"
        >
          Kontakt os
        </button>
      </p>
    </Section>
  </SubpageLayout>
);

export default OmOs;
